
import React from 'react';
import { useThemeClasses } from '../../contexts/PublicFlowContext';

// A simple spinner component for the loading state
const LoaderSpinner = ({ className }: { className?: string }) => (
    <div className={`w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin ${className}`} />
);

// Define variants and sizes using a more structured approach
const buttonStyles = {
    base: 'inline-flex items-center justify-center gap-2 whitespace-nowrap transition-all transform disabled:cursor-not-allowed disabled:opacity-60 disabled:transform-none',
    variants: {
        primary: 'animated-gradient-button text-white font-bold shadow-lg',
        secondary: 'bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-300 font-semibold shadow-sm hover:bg-slate-50 dark:hover:bg-slate-800 hover:border-slate-300 dark:hover:border-slate-700 hover:text-slate-900 dark:hover:text-white',
        ghost: 'text-slate-700 dark:text-slate-400 font-semibold hover:bg-slate-100 dark:hover:bg-slate-800 dark:hover:text-slate-200',
        destructive: 'bg-red-600 text-white font-semibold hover:bg-red-700 shadow',
        link: 'text-slate-600 dark:text-slate-400 hover:text-sky-700 dark:hover:text-sky-400 underline font-semibold bg-transparent shadow-none',
        success: 'bg-green-600 text-white font-bold shadow-lg hover:bg-green-700 transition-all transform hover:scale-105',
    },
    sizes: {
        default: 'px-6 py-3 rounded-md',
        sm: 'px-4 py-2 text-sm rounded-md',
        lg: 'px-8 py-3 rounded-lg',
        xl: 'px-12 py-4 text-lg rounded-lg',
        full: 'w-full p-4 text-lg rounded-lg',
        nav: 'px-6 py-2 rounded-lg',
        sidebar: 'px-4 py-2.5 rounded-lg' // Size to match sidebar items
    },
};

type ButtonOrAnchorProps = React.ButtonHTMLAttributes<HTMLButtonElement> & React.AnchorHTMLAttributes<HTMLAnchorElement>;

export interface ButtonProps extends ButtonOrAnchorProps {
    as?: 'button' | 'a';
    variant?: keyof typeof buttonStyles.variants;
    size?: keyof typeof buttonStyles.sizes;
    isLoading?: boolean;
    leftIcon?: React.ReactNode;
    rightIcon?: React.ReactNode;
}

const Button = React.forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonProps>(
    ({
        className,
        variant = 'primary',
        size = 'default',
        isLoading = false,
        children,
        leftIcon,
        rightIcon,
        as = 'button',
        ...props
    }, ref) => {

        const finalClassName = useThemeClasses([
            buttonStyles.base,
            buttonStyles.variants[variant],
            buttonStyles.sizes[size],
            className
        ].filter(Boolean).join(' '));

        const content = isLoading ? (
            <LoaderSpinner />
        ) : (
            <>
                {leftIcon}
                {children}
                {rightIcon}
            </>
        );

        if (as === 'a') {
            return (
                <a
                    className={finalClassName}
                    ref={ref as React.Ref<HTMLAnchorElement>}
                    {...props}
                >
                    {content}
                </a>
            );
        }

        return (
            <button
                className={finalClassName}
                ref={ref as React.Ref<HTMLButtonElement>}
                disabled={isLoading || (props as React.ButtonHTMLAttributes<HTMLButtonElement>).disabled}
                {...props}
            >
                {content}
            </button>
        );
    }
);
Button.displayName = 'Button';

export { Button };
